import { useEffect, useRef, useState } from "react";
import { EyeOff, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useBubbleSize } from "@/hooks/useBubbleSize";
import { BUBBLE_SIZE_CLASS } from "./bubble";

interface Props {
  stream: MediaStream | null;
  onHide?: () => void;
  /**
   * If true, position via drag/corner-snap. If false (e.g. inside a fixed
   * column of the screen-share split), render inline filling the parent.
   */
  floating?: boolean;
  /** Initial corner when floating. */
  initialCorner?: "tl" | "tr" | "bl" | "br";
  className?: string;
}

/**
 * Local self-view. Mirrors horizontally like a selfie, muted to avoid echo.
 * When `floating`, provides the same drag/resize behaviour as the avatar
 * bubble in the document-with-bubble layout.
 */
export function UserCameraBubble({
  stream,
  onHide,
  floating = true,
  initialCorner = "bl",
  className,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream;
    if (stream) el.play().catch(() => {});
  }, [stream]);

  const {
    containerRef,
    size,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
  } = useBubbleSize();

  const [corner, setCorner] = useState<"tl" | "tr" | "bl" | "br">(initialCorner);
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // The container the bubble is measured against — its parent element. We
  // attach a container ref on the wrapper so `useBubbleSize` observes the
  // parent (its offsetParent) via a wrapping div.
  const outerRef = (node: HTMLDivElement | null) => {
    wrapRef.current = node;
    containerRef(node?.parentElement ?? null);
  };

  const video = (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      className="absolute inset-0 h-full w-full object-cover"
      style={{ transform: "scaleX(-1)" }}
    />
  );

  const hideButton = onHide ? (
    <button
      type="button"
      aria-label="Hide self view"
      className="absolute top-1 right-1 z-30 p-1 rounded-full bg-black/40 text-white hover:bg-black/60"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onHide();
      }}
    >
      <EyeOff className="h-3 w-3" />
    </button>
  ) : null;

  if (!floating) {
    return (
      <div
        className={cn(
          "relative h-full w-full overflow-hidden bg-background",
          className,
        )}
      >
        {video}
        {hideButton}
      </div>
    );
  }

  return (
    <div
      ref={outerRef}
      className={cn(
        "absolute",
        BUBBLE_SIZE_CLASS,
        "rounded-2xl overflow-hidden border-2 border-background shadow-2xl bg-background touch-none select-none cursor-grab active:cursor-grabbing",
        corner === "tl" && "top-4 left-4",
        corner === "tr" && "top-4 right-4",
        corner === "bl" && "bottom-4 left-4",
        corner === "br" && "bottom-4 right-4",
        className,
      )}
      style={{
        ...(size.width > 0 ? { width: size.width, height: size.height } : {}),
        ...(drag
          ? {
              transform: `translate(${drag.dx}px, ${drag.dy}px)`,
              transition: "none",
            }
          : {}),
      }}
      onPointerDown={(e) => {
        dragStateRef.current = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          moved: false,
        };
      }}
      onPointerMove={(e) => {
        const s = dragStateRef.current;
        if (!s || s.pointerId !== e.pointerId) return;
        const dx = e.clientX - s.startX;
        const dy = e.clientY - s.startY;
        if (!s.moved && Math.hypot(dx, dy) < 6) return;
        if (!s.moved) {
          s.moved = true;
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        }
        setDrag({ dx, dy });
      }}
      onPointerUp={(e) => {
        const s = dragStateRef.current;
        dragStateRef.current = null;
        if (!s || !s.moved) {
          setDrag(null);
          return;
        }
        try {
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        const wrap = wrapRef.current;
        const parent = wrap?.parentElement;
        if (wrap && parent) {
          const wr = wrap.getBoundingClientRect();
          const pr = parent.getBoundingClientRect();
          const cx = wr.left + wr.width / 2 - pr.left;
          const cy = wr.top + wr.height / 2 - pr.top;
          const left = cx < pr.width / 2;
          const top = cy < pr.height / 2;
          setCorner(top ? (left ? "tl" : "tr") : left ? "bl" : "br");
        }
        setDrag(null);
      }}
      onPointerCancel={() => {
        dragStateRef.current = null;
        setDrag(null);
      }}
    >
      <div className={drag ? "pointer-events-none h-full w-full" : "h-full w-full"}>
        {video}
      </div>
      {hideButton}
      <button
        type="button"
        aria-label="Resize self view"
        className="absolute bottom-1 right-1 z-30 p-1 rounded-full bg-black/40 text-white hover:bg-black/60 cursor-nwse-resize touch-none"
        onPointerDown={(e) => {
          e.stopPropagation();
          onResizeStart(e);
        }}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
      >
        <Maximize2 className="h-3 w-3" />
      </button>
    </div>
  );
}